import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom"; 
import "./Home.css";

function Home() {
  // State for modals and menu
  const [pricingModal, setPricingModal] = useState({ open: false, plan: "" });
  const [successModal, setSuccessModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const bookingFormRef = useRef();

  // Modal handlers
  const showPricingModal = (plan) => setPricingModal({ open: true, plan });
  const closePricingModal = () => setPricingModal({ open: false, plan: "" });
  const closeSuccessModal = () => setSuccessModal(false);

  // Mobile menu toggle
  const toggleMobileMenu = () => setMobileMenuOpen((open) => !open);

  // Form submit handler
  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setSuccessModal(true);
    bookingFormRef.current.reset();
  };

  // Reveal animation effect for .reveal elements
  useEffect(() => {
    const revealElements = document.querySelectorAll('.reveal');
    const onScroll = () => {
      const windowHeight = window.innerHeight;
      revealElements.forEach(el => {
        const top = el.getBoundingClientRect().top;
        if (top < windowHeight - 100) {
          el.classList.add('active');
        }
      });
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Smooth scroll for anchor links
  useEffect(() => {
    const handleAnchorClick = (e) => {
      if (
        e.target.tagName === "A" &&
        e.target.getAttribute("href") &&
        e.target.getAttribute("href").startsWith("#")
      ) {
        const id = e.target.getAttribute("href").slice(1);
        const el = document.getElementById(id);
        if (el) {
          e.preventDefault();
          const header = document.querySelector(".header");
          const offset = header ? header.offsetHeight : 0;
          window.scrollTo({
            top: el.offsetTop - offset,
            behavior: "smooth",
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Byfotech",
          "description": "Ghana-based technology company delivering cutting-edge digital solutions worldwide. Web development, mobile apps, and advanced tech solutions.",
          "url": "https://byfotech.com",
          "logo": "https://byfotech.com/logo.png",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+233555918676",
            "contactType": "customer service",
            "email": "byfotech@gmail.com",
            "availableLanguage": ["English"]
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Ablekuma, Around Canada House",
            "addressLocality": "Accra",
            "addressCountry": "Ghana"
          },
          "sameAs": [
            "https://bogsoga.org",
            "https://olaqacatholic.org"
          ],
          "serviceType": [
            "Web Development",
            "Mobile App Development",
            "E-Commerce Solutions",
            "Custom Software Development",
            "Cloud Solutions",
            "Tech Consulting"
          ]
        }
        `}</script>
      </Helmet>

      {/* Accessibility skip link */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header */}
      <header className="header" id="header">
        <nav className="nav-container">
          <a href="#home" className="logo">
            ✨ Byfotech
          </a>
          <ul className={`nav-menu${mobileMenuOpen ? " active" : ""}`} id="navMenu">
            <li>
              <a href="#home">Home</a>
            </li>
            <li>
              <a href="#about">About</a>
            </li>
            <li>
              <a href="#services">Services</a>
            </li>
            <li>
              <a href="#portfolio">Portfolio</a>
            </li>
            <li>
              <a href="#contact">Contact</a>
            </li>
              <Link to="/privacy-policy" style={{ color: "#6366f1", textDecoration: "underline" }}>
      Privacy Policy
    </Link>
          </ul>
          <a href="/faq" className="nav-cta"> FAQ ❓</a>
             
          <a href="#booking" className="nav-cta">
            Get Started
          </a>
             
          <div
            className={`mobile-menu${mobileMenuOpen ? " active" : ""}`}
            id="mobileMenu"
            onClick={toggleMobileMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="hero-container">
          <div className="hero-content">
            <h1>🚀 Innovating the Future of Businesses Through Technology</h1>
            <p>
              At Byfotech, we deliver cutting-edge digital solutions that empower businesses worldwide. From web development to mobile apps and advanced tech solutions, we are the team you can trust to turn ideas into reality.
            </p>
            <div className="hero-buttons">
              <a href="#booking" className="btn-primary">
                🔹 Get a Free Consultation
              </a>
              <a href="#services" className="btn-secondary">
                🔹 Start Your Project Today
              </a>
            </div>
          </div>
          <div className="hero-visual">
            <div className="tech-grid">
              <div className="tech-card">
                <span className="tech-icon">🌐</span>
                <div className="tech-label">Web Dev</div>
              </div>
              <div className="tech-card">
                <span className="tech-icon">📱</span>
                <div className="tech-label">Mobile Apps</div>
              </div>
              <div className="tech-card">
                <span className="tech-icon">☁️</span>
                <div className="tech-label">Cloud Solutions</div>
              </div>
              <div className="tech-card">
                <span className="tech-icon">🛒</span>
                <div className="tech-label">E-Commerce</div>
              </div>
              <div className="tech-card">
                <span className="tech-icon">⚙️</span>
                <div className="tech-label">Custom Software</div>
              </div>
              <div className="tech-card">
                <span className="tech-icon">💡</span>
                <div className="tech-label">Consulting</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section about" id="about">
        <div className="container">
          <div className="section-header">
            <h2>About Byfotech</h2>
            <p>Technology is not just our work—it's our passion</p>
          </div>
          <div className="about-content">
            <div className="about-text">
              <h3>Global Vision, Local Expertise</h3>
              <p>
                We are a Ghana-based technology company with a <strong>global vision</strong>, located at Ablekuma (Around Canada House). Our mission is to help businesses, startups, and enterprises harness the power of modern technology to achieve growth, scalability, and innovation.
              </p>
              <p>
                We bring together a team of <strong>skilled developers, designers, and tech consultants</strong> who specialize in creating world-class websites, mobile apps, cloud systems, and digital solutions.
              </p>
              <p>
                With creativity, precision, and innovation at the heart of everything we do, we are dedicated to building technology that <strong>works for your business 24/7</strong>.
              </p>
            </div>
            <div className="stats-grid">
              <div className="stat-card reveal">
                <span className="stat-number">50+</span>
                <span className="stat-label">Projects Completed</span>
              </div>
              <div className="stat-card reveal">
                <span className="stat-number">99%</span>
                <span className="stat-label">Client Satisfaction</span>
              </div>
              <div className="stat-card reveal">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Support Available</span>
              </div>
              <div className="stat-card reveal">
                <span className="stat-number">Global</span>
                <span className="stat-label">Reach</span>
              </div>
            </div>
          </div>
        </div>
      </section>



{/* Our Services Section */}
   
   <section className="section" id="services">
  <div className="container">
    <div className="section-header">
      <h2>Our Services</h2>
      <p>Comprehensive digital solutions to power your business growth</p>
    </div>
    <div className="services-grid">
      <div className="service-card reveal">
        <div className="service-icon">🌐</div>
        <h3>Web Development</h3>
        <p>Professional websites tailored for businesses, schools, NGOs, and corporations.</p>
        <ul className="service-features">
          <li>Fully responsive designs that look great on all devices</li>
          <li>SEO-friendly structures to help your brand rank higher</li>
          <li>Modern frameworks for speed and reliability</li>
          <li>Custom functionality and integrations</li>
        </ul>
      </div>
      <div className="service-card reveal">
        <div className="service-icon">📱</div>
        <h3>Mobile App Development</h3>
        <p>Android and iOS apps designed to connect businesses with customers globally.</p>
        <ul className="service-features">
          <li>Cross-platform development for maximum reach</li>
          <li>Secure, scalable, and user-friendly interfaces</li>
          <li>Built with modern frameworks for performance</li>
          <li>App store optimization and deployment</li>
        </ul>
      </div>
      <div className="service-card reveal">
        <div className="service-icon">🛒</div>
        <h3>E-Commerce Solutions</h3>
        <p>Powerful online shops with secure payment integrations and management tools.</p>
        <ul className="service-features">
          <li>Secure payment gateway integrations</li>
          <li>Inventory management systems</li>
          <li>Customer portals and sales dashboards</li>
          <li>Designed to boost sales and simplify management</li>
        </ul>
      </div>
      <div className="service-card reveal">
        <div className="service-icon">⚙️</div>
        <h3>Custom Software Development</h3>
        <p>Tailored systems for schools, hospitals, companies, and institutions.</p>
        <ul className="service-features">
          <li>Management software and dashboards</li>
          <li>Automation tools for efficiency</li>
          <li>Scalable solutions that grow with your business</li>
          <li>Integration with existing systems</li>
        </ul>
      </div>
      <div className="service-card reveal">
        <div className="service-icon">☁️</div>
        <h3>Cloud & Database Solutions</h3>
        <p>Secure cloud hosting with Supabase, Firebase, and AWS integration.</p>
        <ul className="service-features">
          <li>Real-time databases and authentication systems</li>
          <li>API development and integrations</li>
          <li>Designed for performance and reliability</li>
          <li>Scalable cloud infrastructure</li>
        </ul>
      </div>
      <div className="service-card reveal">
        <div className="service-icon">💡</div>
        <h3>Tech Consulting & Support</h3>
        <p>Expert advice to guide your business in choosing the right technology.</p>
        <ul className="service-features">
          <li>Technology strategy and planning</li>
          <li>Digital transformation for enterprises</li>
          <li>Maintenance and support for long-term success</li>
          <li>Growth-oriented solutions</li>
        </ul>
      </div>
    </div>
  </div>
</section>





      {/* Portfolio Section */}
        
   <section className="section portfolio" id="portfolio">
  <div className="container">
    <div className="section-header">
      <h2>Our Portfolio</h2>
      <p>Showcasing successful projects across various industries</p>
    </div>
    <div className="portfolio-grid">
      <div className="portfolio-card reveal">
        <div className="portfolio-image">🎓</div>
        <div className="portfolio-content">
          <h3>Cloudbeast Voting Sytem</h3>
          <p>Cloud Based Voting System.</p>
          <a href="https://bogsoga.org" target="_blank" rel="noopener noreferrer" className="portfolio-link">View Project →</a>
        </div>
      </div>
      <div className="portfolio-card reveal">
        <div className="portfolio-image">⛪</div>
        <div className="portfolio-content">
          <h3>OLQA Modern Database System</h3>
          <p>Modern church website with event management, donation systems, sermon streaming, and community engagement features.</p>
          <a href="https://olaqacatholic.org" target="_blank" rel="noopener noreferrer" className="portfolio-link">View Project →</a>
        </div>
      </div>
      <div className="portfolio-card reveal">
        <div className="portfolio-image">🏢</div>
        <div className="portfolio-content">
          <h3>Enterprise Solutions</h3>
          <p>Custom business applications for various industries including finance, healthcare, retail, and educational institutions.</p>
          <a href="#booking" className="portfolio-link">Start Your Project →</a>
        </div>
      </div>
      <div className="portfolio-card reveal">
        <div className="portfolio-image">🏥</div>
        <div className="portfolio-content">
          <h3>Healthcare Management</h3>
          <p>Digital solutions for hospitals and clinics including patient management, appointment systems, and medical records.</p>
          <a href="#booking" className="portfolio-link">Learn More →</a>
        </div>
      </div>
      <div className="portfolio-card reveal">
        <div className="portfolio-image">🏪</div>
        <div className="portfolio-content">
          <h3>E-Commerce Platforms</h3>
          <p>Online stores with secure payments, inventory management, and sales analytics for local and international businesses.</p>
          <a href="#booking" className="portfolio-link">Get Started →</a>
        </div>
      </div>
      <div className="portfolio-card reveal">
        <div className="portfolio-image">🏛️</div>
        <div className="portfolio-content">
          <h3>NGO & Non-Profit Sites</h3>
          <p>Websites for non-profit organizations with donation systems, volunteer management, and impact tracking features.</p>
          <a href="#booking" className="portfolio-link">Discuss Project →</a>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Pricing Section */}
      <section className="section pricing" id="pricing">
        <div className="container">
          <div className="section-header">
            <h2>Our Pricing Plans</h2>
            <p>Transparent, competitive pricing for businesses of all sizes</p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card reveal" data-plan="starter">
              <div className="pricing-header">
                <div className="pricing-icon">🚀</div>
                <h3>Starter Package</h3>
                <div className="price">
                  <span className="currency">₵</span>
                  <span className="amount">1,500</span>
                  <span className="period">- ₵5,000</span>
                </div>
                <p className="price-description">Perfect for small businesses and startups</p>
              </div>
              <ul className="features-list">
                <li>✅ Professional Website Design</li>
                <li>✅ Responsive Mobile Design</li>
                <li>✅ Basic SEO Optimization</li>
                <li>✅ Contact Forms & Integration</li>
                <li>✅ 3 Months Free Support</li>
                <li>✅ SSL Certificate Setup</li>
                <li>✅ Social Media Integration</li>
              </ul>
              <button className="pricing-btn" onClick={() => showPricingModal("starter")}>
                Get Started
              </button>
            </div>
            <div className="pricing-card popular reveal" data-plan="professional">
              <div className="popular-badge">Most Popular</div>
              <div className="pricing-header">
                <div className="pricing-icon">⭐</div>
                <h3>Professional Package</h3>
                <div className="price">
                  <span className="currency">₵</span>
                  <span className="amount">5,000</span>
                  <span className="period">- ₵15,000</span>
                </div>
                <p className="price-description">Ideal for growing businesses and enterprises</p>
              </div>
              <ul className="features-list">
                <li>✅ Everything in Starter Package</li>
                <li>✅ Custom Web Application</li>
                <li>✅ Database Integration</li>
                <li>✅ Admin Dashboard</li>
                <li>✅ Payment Gateway Integration</li>
                <li>✅ Advanced SEO & Analytics</li>
                <li>✅ 6 Months Free Support</li>
                <li>✅ Mobile App (Basic)</li>
              </ul>
              <button className="pricing-btn" onClick={() => showPricingModal("professional")}>
                Choose Plan
              </button>
            </div>
            <div className="pricing-card reveal" data-plan="enterprise">
              <div className="pricing-header">
                <div className="pricing-icon">💎</div>
                <h3>Enterprise Package</h3>
                <div className="price">
                  <span className="currency">₵</span>
                  <span className="amount">15,000</span>
                  <span className="period">+</span>
                </div>
                <p className="price-description">Complete solutions for large organizations</p>
              </div>
              <ul className="features-list">
                <li>✅ Everything in Professional Package</li>
                <li>✅ Custom Software Development</li>
                <li>✅ Cloud Infrastructure Setup</li>
                <li>✅ Advanced Security Features</li>
                <li>✅ Multi-platform Mobile Apps</li>
                <li>✅ API Development & Integration</li>
                <li>✅ 12 Months Priority Support</li>
                <li>✅ Dedicated Project Manager</li>
              </ul>
              <button className="pricing-btn" onClick={() => showPricingModal("enterprise")}>
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="section why-choose">
  <div className="container">
    <div className="section-header">
      <h2>Why Choose Byfotech?</h2>
      <p>We deliver excellence through innovation, expertise, and dedicated partnership</p>
    </div>
    <div className="features-grid">
      <div className="feature-card reveal">
        <span className="feature-icon">🌍</span>
        <h3>Global Reach, Local Expertise</h3>
        <p>Based in Ghana, serving clients worldwide. We understand both local markets and global standards.</p>
      </div>
      <div className="feature-card reveal">
        <span className="feature-icon">🚀</span>
        <h3>Innovation-Driven</h3>
        <p>We adopt the latest technologies and frameworks for every project to ensure future-proof solutions.</p>
      </div>
      <div className="feature-card reveal">
        <span className="feature-icon">🎯</span>
        <h3>Custom Solutions</h3>
        <p>Every business is unique; we build tailored solutions that fit your specific needs and goals.</p>
      </div>
      <div className="feature-card reveal">
        <span className="feature-icon">💰</span>
        <h3>Affordable & Transparent</h3>
        <p>High-quality services with transparent pricing and no hidden costs. Great value for your investment.</p>
      </div>
      <div className="feature-card reveal">
        <span className="feature-icon">🤝</span>
        <h3>Dedicated Partnership</h3>
        <p>We walk with you every step of the way, from initial planning to post-launch support and growth.</p>
      </div>
      <div className="feature-card reveal">
        <span className="feature-icon">⚡</span>
        <h3>Fast & Reliable</h3>
        <p>Quick turnaround times without compromising quality. 24/7 support for all our clients.</p>
      </div>
    </div>
  </div>
</section>

      {/* Booking Section */}
      <section className="section booking" id="booking">
        <div className="container">
          <div className="section-header">
            <h2>Start Your Project Today</h2>
            <p>
              Ready to transform your business with cutting-edge technology? Let's discuss your project requirements.
            </p>
          </div>
          <form
            className="booking-form"
            id="bookingForm"
            ref={bookingFormRef}
            onSubmit={handleBookingSubmit}
          >
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input type="text" id="firstName" name="firstName" required placeholder="John" />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input type="text" id="lastName" name="lastName" required placeholder="Doe" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input type="email" id="email" name="email" required placeholder="john@example.com" />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input type="tel" id="phone" name="phone" placeholder="+233 555 123 456" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="company">Company/Organization</label>
              <input type="text" id="company" name="company" placeholder="Your Company Name" />
            </div>
            <div className="form-group">
              <label htmlFor="service">Service Required *</label>
              <select id="service" name="service" required>
                <option value="">Select a service</option>
                <option value="web-development">🌐 Web Development</option>
                <option value="mobile-app">📱 Mobile App Development</option>
                <option value="ecommerce">🛒 E-Commerce Solution</option>
                <option value="custom-software">⚙️ Custom Software Development</option>
                <option value="cloud-solutions">☁️ Cloud & Database Solutions</option>
                <option value="consulting">💡 Tech Consulting & Support</option>
                <option value="other">🔧 Other (Please specify in message)</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="budget">Project Budget (USD)</label>
                <select id="budget" name="budget">
                  <option value="">Select budget range</option>
                  <option value="1000-5000">₵1,000 - ₵5,000</option>
                  <option value="5000-15000">₵5,000 - ₵15,000</option>
                  <option value="15000-50000">₵15,000 - ₵50,000</option>
                  <option value="50000+">₵50,000+</option>
                  <option value="custom">Custom Budget</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="timeline">Project Timeline</label>
                <select id="timeline" name="timeline">
                  <option value="">Select timeline</option>
                  <option value="urgent">⚡ Urgent (1-2 weeks)</option>
                  <option value="normal">📅 Normal (1-2 months)</option>
                  <option value="flexible">🕐 Flexible (3+ months)</option>
                  <option value="discussion">💬 Open for Discussion</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="message">Project Details *</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                required
                placeholder="Please describe your project requirements, goals, specific features you need, target audience, and any other relevant details that will help us understand your vision..."
              ></textarea>
            </div>
            <button type="submit" className="submit-btn" id="submitBtn">
              🚀 Submit Project Request
            </button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section contact" id="contact">
        <div className="container">
          <div className="section-header">
            <h2>Get In Touch</h2>
            <p>Ready to start your next project? Let's build something powerful together.</p>
          </div>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Contact Information</h3>
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div className="contact-details">
                  <h4>Email Address</h4>
                  <p>byfotech@gmail.com</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div className="contact-details">
                  <h4>Phone & WhatsApp</h4>
                  <p>+233 0555918676</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div className="contact-details">
                  <h4>Location</h4>
                  <p>
                    Ablekuma, Accra
                    <br />
                     Ghana
                  </p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">⏰</div>
                <div className="contact-details">
                  <h4>Business Hours</h4>
                  <p>
                    Monday - Friday
                    <br />
                    7:00 AM - 6:00 PM GMT
                  </p>
                </div>
              </div>
            </div>
            <div className="stats-grid">
              <div className="stat-card reveal">
                <span className="stat-number">🌍</span>
                <span className="stat-label">Global Clients</span>
              </div>
              <div className="stat-card reveal">
                <span className="stat-number">⚡</span>
                <span className="stat-label">Fast Delivery</span>
              </div>
              <div className="stat-card reveal">
                <span className="stat-number">🔧</span>
                <span className="stat-label">Expert Team</span>
              </div>
              <div className="stat-card reveal">
                <span className="stat-number">💎</span>
                <span className="stat-label">Premium Quality</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>✨ Byfotech</h3>
            <p>
              Innovating the future of businesses through technology. We deliver cutting-edge digital solutions that empower businesses worldwide to achieve growth, scalability, and innovation.
            </p>
            <div className="social-links">
              <a href="mailto:byfotech@gmail.com" className="social-link" title="Email">
                📧
              </a>
              <a href="tel:+233555918676" className="social-link" title="Phone">
                📱
              </a>
              <a href="https://bogsoga.org" target="_blank" rel="noopener noreferrer" className="social-link" title="Portfolio">
                🌐
              </a>
              <a href="#booking" className="social-link" title="Get Started">
                💼
              </a>
            </div>
          </div>
          <div className="footer-section">
            <h3>Our Services</h3>
            <ul>
              <li>
                <a href="#services">🌐 Web Development</a>
              </li>
              <li>
                <a href="#services">📱 Mobile App Development</a>
              </li>
              <li>
                <a href="#services">🛒 E-Commerce Solutions</a>
              </li>
              <li>
                <a href="#services">⚙️ Custom Software</a>
              </li>
              <li>
                <a href="#services">☁️ Cloud Solutions</a>
              </li>
              <li>
                <a href="#services">💡 Tech Consulting</a>
              </li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Industries We Serve</h3>
            <ul>
              <li>
                <a href="#portfolio">🎓 Education & Schools</a>
              </li>
              <li>
                <a href="#portfolio">🏥 Healthcare & Medical</a>
              </li>
              <li>
                <a href="#portfolio">💰 Finance & Banking</a>
              </li>
              <li>
                <a href="#portfolio">🛍️ E-Commerce & Retail</a>
              </li>
              <li>
                <a href="#portfolio">🏠 Real Estate</a>
              </li>
              <li>
                <a href="#portfolio">🏛️ Non-Profit & NGOs</a>
              </li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Contact Details</h3>
            <p>
              <strong>📧 Email:</strong>
              <br />
              byfotech@gmail.com
            </p>
            <p>
              <strong>📞 Phone:</strong>
              <br />
              +233 0555918676
            </p>
            <p>
              <strong>📍 Address:</strong>
              <br />
              Ablekuma, Accra
              <br />
              Around Canada House
              <br />
              Ghana, West Africa
            </p>
            <p>
              <strong>🕒 Hours:</strong>
              <br />
              Mon-Fri: 8AM - 6PM GMT
            </p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>
          &copy; 2019 - {new Date().getFullYear()} <a href="https://byfotech.com" target="_blank" rel="noopener noreferrer" style={{ color: '#ffff00', textDecoration: 'none' }}>Byfotech</a>. All rights reserved.| Innovating the Future of Businesses Through Technology |  🇬🇭
          </p>
         
              <Link to="/privacy-policy" style={{ color: "#6366f1", textDecoration: "underline" }}>
      Privacy Policy
    </Link>
        </div>
      </footer>

      {/* Pricing Modal */}
      {pricingModal.open && (
        <div className="modal" id="pricingModal" style={{ display: "block" }}>
          <div className="modal-content pricing-modal">
            <button className="close-modal" id="closePricingModal" onClick={closePricingModal}>
              &times;
            </button>
            <div id="modalContent">
              <h3>{pricingModal.plan.charAt(0).toUpperCase() + pricingModal.plan.slice(1)} Plan Details</h3>
              <p>Contact us for more information about the {pricingModal.plan} plan.</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModal && (
        <div className="modal" id="successModal" style={{ display: "block" }}>
          <div className="modal-content">
            <button className="close-modal" id="closeModal" onClick={closeSuccessModal}>
              &times;
            </button>
            <h3>🎉 Thank You!</h3>
            <p>
              Your project request has been submitted successfully. We'll review your requirements and get back to you within <strong>24 hours</strong>.
            </p>
            <br />
            <p>
              <strong>Next Steps:</strong>
            </p>
            <p>
              ✅ We'll analyze your project requirements
              <br />
              ✅ Schedule a free consultation call
              <br />
              ✅ Provide a detailed proposal and timeline
              <br />
              ✅ Start bringing your vision to life
            </p>
            <br />
            <p>Keep an eye on your inbox for our response!</p>
          </div>
        </div>
      )}
    </>
  );
}

export default Home;